/**
 * Knowledge Base API Service
 * Supabase client methods for knowledge base operations
 */

import { supabase } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import type { Inserts, Updates } from '@/lib/supabase';
import type { Json } from '@/lib/database.types';
import type { PostgrestError } from '@supabase/supabase-js';
import {
  KBArticle,
  KBArticleWithCategory,
  KBArticleSearchResult,
  KBArticleFeedback,
  KBCategory,
  KBSearchParams,
  KBListFilters,
  KBArticleFormData,
  KBArticleStats,
  KBAdminStats,
  KBMetadata,
} from '@/types/knowledgeBase.types';

type ServiceResult<T> = {
  data: T;
  error: PostgrestError | null;
};

// Explicit column lists for every KB read (FIX-PLAN P10 / WS5 D2). The help pages are public, and
// `select('*')` shipped `search_vector` and the authors' `created_by`/`updated_by` ids to every visitor.
const KB_CATEGORY_COLUMNS =
  'id, name, description, icon, sort_order, parent_category_id, is_active, created_at, updated_at';
const KB_ARTICLE_COLUMNS =
  'id, title, slug, content, excerpt, category_id, tags, is_published, is_featured, view_count, helpful_count, not_helpful_count, related_articles, context_ids, metadata, content_type, quick_tips, faqs, troubleshooting, role_specific_tips, structured_data, created_at, updated_at, published_at';
const KB_ARTICLE_WITH_CATEGORY = `${KB_ARTICLE_COLUMNS}, category:kb_categories(${KB_CATEGORY_COLUMNS})` as const;

const toOptionalArray = (values: string[]): string[] | null =>
  values.length > 0 ? values : null;

const toOptionalArrayUpdate = (values?: string[]): string[] | null | undefined => {
  if (values === undefined) {
    return undefined;
  }
  return values.length > 0 ? values : null;
};

const toMetadataInsert = (metadata: KBMetadata): Json | null =>
  Object.keys(metadata).length > 0 ? (metadata as Json) : null;

const toMetadataUpdate = (metadata?: KBMetadata): Json | null | undefined => {
  if (metadata === undefined) {
    return undefined;
  }
  return Object.keys(metadata).length > 0 ? (metadata as Json) : null;
};

/**
 * Search knowledge base articles using full-text search
 */
export async function searchKBArticles(
  params: KBSearchParams
): Promise<ServiceResult<KBArticleSearchResult[] | null>> {
  const { data, error } = await supabase.rpc('search_kb_articles', {
    p_search_query: params.query,
    p_category_id: params.category_id ?? undefined,
    p_tags: params.tags && params.tags.length > 0 ? params.tags : undefined,
    p_context_id: params.context_id ?? undefined,
    p_only_published: params.only_published !== false,
    p_limit: params.limit || 20,
    p_offset: params.offset || 0,
  });

  return { data: data as KBArticleSearchResult[] | null, error };
}

/**
 * Get a single KB article by ID or slug
 */
export async function getKBArticle(
  idOrSlug: string,
  bySlug = false
): Promise<ServiceResult<KBArticleWithCategory | null>> {
  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_WITH_CATEGORY)
    .eq(bySlug ? 'slug' : 'id', idOrSlug)
    .single<KBArticleWithCategory>();

  return { data, error };
}

/**
 * Get KB articles with filters and sorting
 */
export async function getKBArticles(
  filters: KBListFilters = {}
): Promise<ServiceResult<KBArticleWithCategory[] | null>> {
  let query = supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_WITH_CATEGORY);

  // Apply filters
  if (filters.category_id) {
    query = query.eq('category_id', filters.category_id);
  }

  if (filters.is_published !== undefined) {
    query = query.eq('is_published', filters.is_published);
  }

  if (filters.is_featured !== undefined) {
    query = query.eq('is_featured', filters.is_featured);
  }

  if (filters.tags && filters.tags.length > 0) {
    query = query.overlaps('tags', filters.tags);
  }

  if (filters.context_id) {
    query = query.contains('context_ids', [filters.context_id]);
  }

  // Apply sorting
  const sortBy = filters.sort_by || 'published_at';
  const sortOrder = filters.sort_order || 'desc';
  
  if (sortBy === 'views') {
    query = query.order('view_count', { ascending: sortOrder === 'asc' });
  } else if (sortBy === 'helpful') {
    query = query.order('helpful_count', { ascending: sortOrder === 'asc' });
  } else if (sortBy === 'title') {
    query = query.order('title', { ascending: sortOrder === 'asc' });
  } else if (sortBy === 'updated_date') {
    query = query.order('updated_at', { ascending: sortOrder === 'asc' });
  } else {
    query = query.order('published_at', { ascending: sortOrder === 'asc' });
  }

  // Apply pagination
  if (filters.limit) {
    query = query.limit(filters.limit);
  }
  if (filters.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);
  }

  const { data, error } = await query;
  return { data: data as KBArticleWithCategory[] | null, error };
}

/**
 * Get KB categories with optional article counts
 */
export async function getKBCategories(
  includeInactive = false
): Promise<ServiceResult<KBCategory[] | null>> {
  let query = supabase
    .from('kb_categories')
    .select(KB_CATEGORY_COLUMNS)
    .order('sort_order', { ascending: true });

  if (!includeInactive) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;
  return { data: data as KBCategory[] | null, error };
}

/**
 * Get a single category by ID
 */
export async function getKBCategory(
  id: string
): Promise<ServiceResult<KBCategory | null>> {
  const { data, error } = await supabase
    .from('kb_categories')
    .select(KB_CATEGORY_COLUMNS)
    .eq('id', id)
    .single<KBCategory>();

  return { data, error };
}

/**
 * Get featured articles
 */
export async function getFeaturedArticles(
  limit = 5
): Promise<ServiceResult<KBArticleWithCategory[] | null>> {
  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_WITH_CATEGORY)
    .eq('is_published', true)
    .eq('is_featured', true)
    .order('published_at', { ascending: false })
    .limit(limit);

  return { data: data as KBArticleWithCategory[] | null, error };
}

/**
 * Get related articles for a given article
 */
export async function getRelatedArticles(
  articleId: string
): Promise<ServiceResult<KBArticleWithCategory[]>> {
  // First get the article to find its related_articles array
  const { data: article, error: articleError } = await getKBArticle(articleId);
  
  if (articleError || !article || !article.related_articles || article.related_articles.length === 0) {
    return { data: [], error: articleError };
  }

  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_WITH_CATEGORY)
    .in('id', article.related_articles)
    .eq('is_published', true)
    .limit(5);

  return { data: (data ?? []) as KBArticleWithCategory[], error };
}

/**
 * Track article view
 */
export async function trackKBArticleView(
  articleId: string,
  contextId?: string,
  referrer?: string
): Promise<ServiceResult<Record<string, unknown> | null>> {
  const sessionId = getOrCreateSessionId();
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase.rpc('increment_kb_article_views', {
    p_article_id: articleId,
    p_user_id: user?.id ?? undefined,
    p_session_id: sessionId,
    p_context_id: contextId ?? undefined,
    p_referrer: referrer ?? undefined,
  });

  return { data: data as Record<string, unknown> | null, error };
}

/**
 * Submit article feedback
 */
export async function submitKBArticleFeedback(
  articleId: string,
  isHelpful: boolean,
  feedbackText?: string
): Promise<ServiceResult<KBArticleFeedback | null>> {
  const sessionId = getOrCreateSessionId();
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('kb_article_feedback')
    .upsert({
      article_id: articleId,
      user_id: user?.id || null,
      session_id: sessionId,
      is_helpful: isHelpful,
      feedback_text: feedbackText || null,
    })
    .select()
    .single<KBArticleFeedback>();

  return { data, error };
}

/**
 * Get user's feedback for an article
 */
export async function getKBArticleFeedback(
  articleId: string
): Promise<ServiceResult<KBArticleFeedback | null>> {
  const sessionId = getOrCreateSessionId();
  const { data: { user } } = await supabase.auth.getUser();

  const query = supabase
    .from('kb_article_feedback')
    .select('*')
    .eq('article_id', articleId);

  if (user) {
    query.eq('user_id', user.id);
  } else {
    query.eq('session_id', sessionId);
  }

  const { data, error } = await query.maybeSingle<KBArticleFeedback>();
  return { data, error };
}

/**
 * Get article statistics
 */
export async function getKBArticleStats(
  articleId: string
): Promise<ServiceResult<KBArticleStats | null>> {
  const { data: article, error: articleError } = await getKBArticle(articleId);
  
  if (articleError || !article) {
    return { data: null, error: articleError };
  }

  // Get views in last 30 days
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { count: recentViewsCount, error: viewsError } = await supabase
    .from('kb_article_views')
    .select('id', { count: 'exact', head: true })
    .eq('article_id', articleId)
    .gte('viewed_at', thirtyDaysAgo.toISOString());

  if (viewsError) {
    return { data: null, error: viewsError };
  }

  // Get average time spent
  const { data: timeData, error: timeError } = await supabase
    .from('kb_article_views')
    .select('time_spent_seconds')
    .eq('article_id', articleId)
    .not('time_spent_seconds', 'is', null);

  if (timeError) {
    return { data: null, error: timeError };
  }

  const avgTimeSpent = timeData && timeData.length > 0
    ? timeData.reduce((sum, v) => sum + (v.time_spent_seconds || 0), 0) / timeData.length
    : null;

  const totalFeedback = article.helpful_count + article.not_helpful_count;
  const helpfulPercentage = totalFeedback > 0
    ? (article.helpful_count / totalFeedback) * 100
    : 0;

  return {
    data: {
      total_views: article.view_count,
      helpful_count: article.helpful_count,
      not_helpful_count: article.not_helpful_count,
      helpful_percentage: Math.round(helpfulPercentage * 10) / 10,
      average_time_spent: avgTimeSpent ? Math.round(avgTimeSpent) : null,
      views_last_30_days: recentViewsCount ?? 0,
    },
    error: null,
  };
}

// ==================== ADMIN FUNCTIONS ====================

/**
 * Create a new KB article (admin only)
 */
export async function createKBArticle(
  article: KBArticleFormData
): Promise<ServiceResult<KBArticle | null>> {
  const payload: Inserts<'knowledge_base_articles'> = {
    title: article.title,
    slug: article.slug,
    content: article.content,
    category_id: article.category_id,
    excerpt: article.excerpt || null,
    tags: article.tags,
    is_published: article.is_published,
    is_featured: article.is_featured,
    related_articles: toOptionalArray(article.related_articles),
    context_ids: toOptionalArray(article.context_ids),
    metadata: toMetadataInsert(article.metadata),
    content_type: article.content_type ?? 'article',
    quick_tips: article.quick_tips ? (article.quick_tips as unknown as Json) : [],
    faqs: article.faqs ? (article.faqs as unknown as Json) : [],
    troubleshooting: article.troubleshooting ? (article.troubleshooting as unknown as Json) : [],
    role_specific_tips: article.role_specific_tips ? (article.role_specific_tips as unknown as Json) : {},
    structured_data: article.structured_data ? (article.structured_data as unknown as Json) : null,
  };

  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .insert(payload)
    .select()
    .single<KBArticle>();

  return { data, error };
}

/**
 * Update an existing KB article (admin only)
 */
export async function updateKBArticle(
  id: string,
  updates: Partial<KBArticleFormData>
): Promise<ServiceResult<KBArticle | null>> {
  const payload: Updates<'knowledge_base_articles'> = {};

  if (updates.title !== undefined) payload.title = updates.title;
  if (updates.slug !== undefined) payload.slug = updates.slug;
  if (updates.content !== undefined) payload.content = updates.content;
  if (updates.category_id !== undefined) payload.category_id = updates.category_id;
  if (updates.excerpt !== undefined) payload.excerpt = updates.excerpt || null;
  if (updates.tags !== undefined) payload.tags = updates.tags;
  if (updates.is_published !== undefined) payload.is_published = updates.is_published;
  if (updates.is_featured !== undefined) payload.is_featured = updates.is_featured;
  if (updates.related_articles !== undefined) {
    payload.related_articles = toOptionalArrayUpdate(updates.related_articles);
  }
  if (updates.context_ids !== undefined) {
    payload.context_ids = toOptionalArrayUpdate(updates.context_ids);
  }
  if (updates.metadata !== undefined) {
    payload.metadata = toMetadataUpdate(updates.metadata);
  }
  if (updates.content_type !== undefined) {
    payload.content_type = updates.content_type;
  }
  if (updates.quick_tips !== undefined) {
    payload.quick_tips = updates.quick_tips as unknown as Json;
  }
  if (updates.faqs !== undefined) {
    payload.faqs = updates.faqs as unknown as Json;
  }
  if (updates.troubleshooting !== undefined) {
    payload.troubleshooting = updates.troubleshooting as unknown as Json;
  }
  if (updates.role_specific_tips !== undefined) {
    payload.role_specific_tips = updates.role_specific_tips as unknown as Json;
  }
  if (updates.structured_data !== undefined) {
    payload.structured_data = updates.structured_data as unknown as Json;
  }

  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .update(payload)
    .eq('id', id)
    .select()
    .single<KBArticle>();

  return { data, error };
}

/**
 * Delete a KB article (admin only)
 */
export async function deleteKBArticle(
  id: string
): Promise<ServiceResult<null>> {
  const { error } = await supabase
    .from('knowledge_base_articles')
    .delete()
    .eq('id', id);

  return { data: null, error };
}

/**
 * Create a new KB category (admin only)
 */
export async function createKBCategory(
  category: Inserts<'kb_categories'>
): Promise<ServiceResult<KBCategory | null>> {
  const { data, error } = await supabase
    .from('kb_categories')
    .insert(category)
    .select()
    .single<KBCategory>();

  return { data, error };
}

/**
 * Update a KB category (admin only)
 */
export async function updateKBCategory(
  id: string,
  updates: Updates<'kb_categories'>
): Promise<ServiceResult<KBCategory | null>> {
  const { data, error } = await supabase
    .from('kb_categories')
    .update(updates)
    .eq('id', id)
    .select()
    .single<KBCategory>();

  return { data, error };
}

/**
 * Delete a KB category (admin only)
 */
export async function deleteKBCategory(
  id: string
): Promise<ServiceResult<null>> {
  const { error } = await supabase
    .from('kb_categories')
    .delete()
    .eq('id', id);

  return { data: null, error };
}

/**
 * Get admin statistics
 */
export async function getKBAdminStats(): Promise<ServiceResult<KBAdminStats | null>> {
  const { data: articlesData, error: articlesError } = await supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_COLUMNS);

  if (articlesError || !articlesData) {
    return { data: null, error: articlesError };
  }

  const articles = articlesData as KBArticle[];

  const totalArticles = articles.length;
  const stats = articles.reduce(
    (acc, a) => {
      if (a.is_published) acc.published++;
      acc.views += a.view_count;
      acc.helpful += a.helpful_count;
      acc.feedback += a.helpful_count + a.not_helpful_count;
      return acc;
    },
    { published: 0, views: 0, helpful: 0, feedback: 0 }
  );

  const publishedArticles = stats.published;
  const draftArticles = totalArticles - publishedArticles;
  const totalViews = stats.views;
  
  const totalFeedback = stats.feedback;
  const totalHelpful = stats.helpful;
  const helpfulPercentage = totalFeedback > 0
    ? (totalHelpful / totalFeedback) * 100
    : 0;

  const mostViewedArticles = [...articles]
    .sort((a, b) => b.view_count - a.view_count)
    .slice(0, 5)
    .map(a => ({
      id: a.id,
      title: a.title,
      view_count: a.view_count,
    }));

  const mostHelpfulArticles = [...articles]
    .filter(a => (a.helpful_count + a.not_helpful_count) > 0)
    .map(a => ({
      id: a.id,
      title: a.title,
      helpful_percentage: ((a.helpful_count / (a.helpful_count + a.not_helpful_count)) * 100),
    }))
    .sort((a, b) => b.helpful_percentage - a.helpful_percentage)
    .slice(0, 5);

  const recentArticles = [...articles]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 10);

  const { data: categories, error: categoriesError } = await getKBCategories(true);
  if (categoriesError) {
    return { data: null, error: categoriesError };
  }

  return {
    data: {
      total_articles: totalArticles,
      published_articles: publishedArticles,
      draft_articles: draftArticles,
      total_categories: categories?.length || 0,
      total_views: totalViews,
      total_feedback: totalFeedback,
      helpful_percentage: Math.round(helpfulPercentage * 10) / 10,
      most_viewed_articles: mostViewedArticles,
      most_helpful_articles: mostHelpfulArticles,
      recent_articles: recentArticles,
    },
    error: null,
  };
}

// ==================== HELP CONTENT FUNCTIONS ====================

/**
 * Fetch a help context article by context_id.
 * Returns the first published help_context article whose context_ids contains the given id.
 */
export async function fetchHelpContext(
  contextId: string
): Promise<ServiceResult<KBArticle | null>> {
  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_COLUMNS)
    .filter('content_type', 'eq', 'help_context')
    .eq('is_published', true)
    .contains('context_ids', [contextId])
    .limit(1)
    .maybeSingle<KBArticle>();

  return { data, error };
}

/**
 * Fetch a reference data article by slug.
 * Used for document requirements, rejection reasons, photography tips, etc.
 */
export async function fetchReferenceData(
  slug: string
): Promise<ServiceResult<KBArticle | null>> {
  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_COLUMNS)
    .filter('content_type', 'eq', 'reference_data')
    .eq('slug', slug)
    .eq('is_published', true)
    .maybeSingle<KBArticle>();

  return { data, error };
}

/**
 * Search all help content (articles, help contexts, reference data) via full-text or ILIKE.
 * Returns published articles matching the query, prioritising title matches.
 */
export async function searchHelpContentFromDB(
  query: string,
  contextId?: string,
  limit = 20
): Promise<ServiceResult<KBArticle[] | null>> {
  // Sanitize input: strip characters that break PostgREST .or() filter syntax
  const sanitized = query.replace(/[(),.'"\\]/g, ' ').trim();
  if (!sanitized) return { data: [], error: null };
  const likePattern = `%${sanitized}%`;

  const q = supabase
    .from('knowledge_base_articles')
    .select(KB_ARTICLE_COLUMNS)
    .eq('is_published', true)
    .or(`title.ilike.${likePattern},excerpt.ilike.${likePattern},content.ilike.${likePattern}`)
    .order('view_count', { ascending: false })
    .limit(limit);

  if (contextId) {
    // Boost context-relevant results by querying separately — but for simplicity
    // we include all and let the client sort by relevance
  }

  const { data, error } = await q;
  return { data: data as KBArticle[] | null, error };
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Get or create anonymous session ID for tracking
 */
function getOrCreateSessionId(): string {
  const SESSION_KEY = 'kb_session_id';
  let sessionId = localStorage.getItem(SESSION_KEY);

  if (!sessionId) {
    sessionId = `session_${uuidv4()}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }

  return sessionId;
}

/**
 * Generate URL-friendly slug from title
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Estimate reading time in minutes
 */
export function estimateReadingTime(content: string): number {
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
}
